import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrganisationAutocomplete from 'components/OrganisationPicker/OrganisationAutocomplete';
import {
  TextV2,
  TextAreaV2,
  Dialog,
} from '@insights-ltd/design-library/components';
import { Box, styled } from '@mui/material';
import { GroupResponse, OrganisationResponse } from 'api';
import AccountOrganisationCard from 'components/OrganisationInfoCard/AccountOrganisationCard/AccountOrganisationCard';
import LinkButton from 'components/LinkButton';
import { useAuth } from 'contexts/AuthContext';
import { isSuperAdmin } from 'utils/role';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

type ChangeOrgDialogProps = {
  open: boolean;
  onClose: () => void;
  currentOrg: OrganisationResponse;
  currentGroup?: GroupResponse;
  currentOrganisationCard?: React.ReactElement;
  handleOrganisationChange: (orgId: string, reason: string) => void;
};

const StyledLabel = styled('label')({ marginBottom: '0.25rem !important' });

const ChangeOrgDialog = ({
  open,
  onClose,
  currentGroup,
  currentOrganisationCard: CurrentOrganisation,
  handleOrganisationChange,
}: ChangeOrgDialogProps) => {
  const { t } = useTranslation();
  const [selectedOrg, setSelectedOrg] = useState<
    OrganisationResponse | undefined
  >();
  const [reason, setReason] = useState('');
  const { user } = useAuth();
  const userIsSuperAdmin = isSuperAdmin(user?.role);
  const noSelection = selectedOrg === undefined;

  const newOrganisationElement = selectedOrg ? (
    <>
      <StyledLabel htmlFor="organisation-search">
        <TextV2 component="span" variant="bodyBold">
          {t('ui.event-management.learners.organisation.new-organisation')}
        </TextV2>
      </StyledLabel>
      <AccountOrganisationCard
        id={selectedOrg.id}
        name={selectedOrg.name}
        dataRetentionPolicy={selectedOrg.dataRetentionPolicy}
        variant="small"
        sx={{ paddingTop: '4px' }}
        parent={currentGroup}
        titleAction={
          <LinkButton
            sx={{
              minWidth: '50px',
              padding: '0 !important',
              justifySelf: 'flex-end',
              alignSelf: 'flex-end',
            }}
            onClick={() => setSelectedOrg(undefined)}
          >
            {t('ui.event-management.learners.organisation.change')}
          </LinkButton>
        }
      />
    </>
  ) : (
    <OrganisationAutocomplete
      groupId={userIsSuperAdmin ? undefined : currentGroup?.id}
      onSelect={(organisation) => setSelectedOrg(organisation)}
      currentOrg={selectedOrg}
      disableClearable
      labelKey="ui.event-management.learners.organisation.new-organisation"
    />
  );

  return (
    <Dialog
      title={t('ui.event-management.learners.organisation.change-org.title')}
      open={open}
      onClose={onClose}
      content={
        <>
          <Box
            component="div"
            sx={(theme) => ({
              marginBottom: theme.spacing(spacingSizeMap.S),
            })}
          >
            <StyledLabel htmlFor="organisation-search">
              <TextV2 component="span" variant="bodyBold">
                {t('ui.event-management.learner.organisation')}
              </TextV2>
            </StyledLabel>
            {CurrentOrganisation}
          </Box>
          <Box
            component="div"
            sx={(theme) => ({
              marginBottom: theme.spacing(spacingSizeMap.S),
            })}
          >
            {newOrganisationElement}
          </Box>
          <Box component="div">
            <TextAreaV2
              maxLength={500}
              fullWidth
              multiline
              minRows={3}
              label={t('ui.event-management.learner.why-moving')}
              variant="outlined"
              placeholder={t('ui.event-management.learner.provide-reason')}
              disabled={noSelection}
              onChange={(event) => setReason(event.target.value)}
              hideCount
            />
          </Box>
        </>
      }
      primaryButtonText={t(
        'ui.event-management.learners.organisation.change-org',
      )}
      onSubmit={() => {
        onClose();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        handleOrganisationChange(selectedOrg!.id, reason);
      }}
      disablePrimaryButton={noSelection}
    />
  );
};

export default ChangeOrgDialog;
